/**
 * Trim the input value and return it
 * @returns {str}
 */
export const trimInput = (str: string | null): string | null => {
    if (str === null || str === '') {
        return str;
    }
    return str.trim().replace(/\s+/g, '');
};

/**
 * Parse a string to a boolean value.
 * Throws an error if the string is not 'true' or 'false'.
 *
 * @param data - The string data to parse
 * @param meta - Metadata about the data
 * @returns The parsed boolean value
 * @throws {Error} If the string is not 'true' or 'false'
 */
export const parseStringToBoolean = (data: unknown, meta: any): boolean => {
    if (typeof data !== 'string') {
        throw new Error(`Environment variables are not set correctly for ${meta.path[0]}`);
    }
    const lowerCaseData = data.toLowerCase();
    if (lowerCaseData === 'true') {
        return true;
    } else if (lowerCaseData === 'false') {
        return false;
    } else {
        throw new Error(`Environment variables are not set correctly for ${meta.path[0]}`);
    }
};

/**
 * Converts a string of filter parameters into an object.
 * Each parameter is expected to be in the format "key=value" separated by '&'.
 * If the input string is empty or null, an empty object is returned.
 * @param filterParams The string containing filter parameters.
 * @returns An object containing key-value pairs extracted from the filter parameters string.
 */

export const convertFilterParamsToObject = (filterParams = '') => {
    const obj: any = {};
    if (filterParams) {
        const filterParamsPairs = filterParams.split('&');
        filterParamsPairs.forEach((pair) => {
            // Splitting each pair by '=' to separate the key and value
            const keyValue = pair.split('=');
            if (keyValue.length === 2) {
                obj[keyValue[0]] = keyValue[1];
            }
        });
    }
    return obj;
};

/**
 * Extract string variables based on a pattern search.
 * @param {string} search Search pattern to provide. By default it search for [].
 * @param {RegExp} regexpSearch
 * @returns {string[]} Array of matched string
 * @example
 * const input = 'Hello [name]';
 * const variables = extractString(input);
 * // variables = ['name']
 */
export const extractString = (search: string, regexpSearch = /\[([^[\]]+)\]/g): string[] => {
    const matches = search.match(regexpSearch);
    if (!matches) {
        return [];
    }

    const variables = matches.map((match) => match.slice(1, -1));
    return variables;
};

/**
 * Takes an array, filters out any undefined items, and returns a string
 * where the remaining elements are joined together with a " | " delimiter.
 *
 * @param {Array<any>} array - The input array containing elements of any type.
 * @returns {string} A string representation of the array with undefined items removed,
 * and the remaining elements separated by " | ".
 *
 * @example
 * const inputArray = [1, undefined, 'hello', , 3, 'world'];
 * const result = filterAndSeperateArray(inputArray);
 * console.log(result); // Output: "1 | hello | 3 | world"
 */
export const filterAndSeperateArray = (array: Array<string>): string => {
    return array.filter((item) => typeof item !== 'undefined').join(' | ');
};

/**
 * Function to convert any string with dash seperated words with camelCase convntions.
 * @param {string} str - The string to convert
 * @returns {string}
 */
export const dashToCamelCase = (str: string) => {
    return str.replace(/-([a-z])/g, (match, group1) => group1.toUpperCase());
};

/**
 * Function to convert any string with dash or underscore seperated words with camelCase convntions.
 * @param {string} str - The string to convert
 * @returns {string}
 */
export const dashUnderScoreToCamelCase = (str: string) => {
    return str.replace(/[-_](\w)/g, (match, group1) => group1.toUpperCase());
};

/* Function to capitalize the first letter of a string and make the rest of the string lowercase.
 * @param {string} str - The input string to be transformed.
 * @returns {string} - The transformed string with the first letter capitalized and the rest in lowercase.
 */
export const capitalizeFirstLetter = (str = '') => {
    if (str.length === 0) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Truncates the first word of a given string to a specified length and maintains the rest of the string.
 * @param {string} str - The full string to modify.
 * @param {number} [length=3] - The length to which the first word should be truncated.
 * @returns {string} The string with the first word truncated and the rest unchanged.
 */
export const truncateFirstWord = (str: string, length = 3) => {
    const words = str.split(' ');
    if (words.length === 0) return ''; // Return empty string if no words found

    const firstWord = words[0].substring(0, length); // Truncate the first word
    words[0] = firstWord; // Replace the first word in the array with the truncated version

    return words.join(' '); // Reassemble the string
};

/**
 * Wraps a specified number of words in a string with HTML span tags.
 * @param {string} str - The original string to manipulate.
 * @param {number} numWords - The number of words to wrap.
 * @param {string} direction - The direction from which to count the words ('start' or 'end').
 * @returns {string} The modified string with specified words wrapped in a span.
 */
export const wrapWords = (str: string, numWords: number, direction = 'start') => {
    const words = str.split(' ');
    if (words.length === 0) return '';

    let wrappedWords;

    if (direction === 'start') {
        wrappedWords = words.slice(0, numWords).join(' ');
        return `<b>${wrappedWords}</b> ` + words.slice(numWords).join(' ');
    } else if (direction === 'end') {
        wrappedWords = words.slice(-numWords).join(' ');
        return words.slice(0, words.length - numWords).join(' ') + ` <b>${wrappedWords}</b>`;
    }

    return str; // Return the original string if direction is not valid
};

/**
 * Formats a string representation of a number to two decimal places.
 *
 * This function ensures that the input string is formatted to always display two decimal places.
 * If the input already includes a decimal point, it pads or truncates the decimal part to exactly two digits.
 * If no decimal point is present, ".00" is appended to the input.
 *
 * @param {string} input - The string representation of the number to format.
 * @returns {string} The formatted string with two decimal places.
 */
export const formatStringToTwoDecimals = (input: string) => {
    if (input.includes('.')) {
        const [integerPart, decimalPart] = input.split('.');
        const formattedDecimalPart = decimalPart.padEnd(2, '0').slice(0, 2);
        return `${integerPart}.${formattedDecimalPart}`;
    } else {
        return `${input}.00`;
    }
};
